<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-app-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center px-4 h-14">
            <div class="h-5 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div class="pb-20 px-4 pt-6 space-y-6">
          <div class="space-y-3">
            <div class="h-2.5 w-28 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden px-4 py-3">
              <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
              <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-4">
              <div class="h-28 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded content -->
    <template v-else>
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center px-4 h-14">
          <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Embed Code</h1>
        </div>
      </div>

      <div class="pb-20 pt-6 px-4 space-y-6">

        <!-- Language -->
        <div>
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Language</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden px-4 py-3 dark:text-gray-400">
            <TimelydoDropdown
              title=""
              name="language"
              :selected_value="selectedLanguage"
              :values="useSettingsStoreObj().languages_list"
              :opts="languageDropdownOptions"
              @change="selectLanguage"
            />
          </div>
        </div>

        <!-- Embed code -->
        <div>
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Embed code</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-4 relative">
            <TimelydoTextArea :value="embed_profile_code"
                              rows="5"
                              :placeholder="I18N.website_content.account_settings.embed.title"
                              name="user[embed code]"
                              :opts="{id: 'embed_code_text_mobile', disabled: true}" />
            <button @click="copyText"
                    class="absolute top-5 right-5 flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-medium hover:underline">
              {{ I18N.website_content.account_settings.qr_code.copy }}
              <svg class="w-3.5 h-3.5 fill-blue-600 dark:fill-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M20 2H10c-1.103 0-2 .897-2 2v4H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2v-4h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM4 20V10h10l.002 10H4zm16-6h-4v-4c0-1.103-.897-2-2-2h-4V4h10v10z"/>
              </svg>
            </button>
          </div>
        </div>

      </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6 space-y-4">
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
              <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-3.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="px-6 py-4 max-w-xs">
              <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
              <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
              <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="px-6 py-4">
              <div class="h-28 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded content -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Embed Code</h1>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Developers Console</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-5xl mx-auto px-6 py-6 space-y-4">

        <!-- Language card -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
            <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Language</h2>
          </div>
          <div class="px-6 py-4 max-w-xs dark:text-gray-400">
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
              {{ I18N.website_content.account_settings.embed.select_language }}
            </label>
            <TimelydoDropdown
              title=""
              name="language"
              :selected_value="selectedLanguage"
              :values="useSettingsStoreObj().languages_list"
              :opts="languageDropdownOptions"
              @change="selectLanguage"
            />
          </div>
        </div>

        <!-- Embed code card -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div class="flex items-center gap-2.5">
              <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
              <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Embed code</h2>
            </div>
            <button @click="copyText"
                    class="flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
              {{ I18N.website_content.account_settings.qr_code.copy }}
              <svg class="w-3.5 h-3.5 fill-blue-600 dark:fill-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M20 2H10c-1.103 0-2 .897-2 2v4H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2v-4h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM4 20V10h10l.002 10H4zm16-6h-4v-4c0-1.103-.897-2-2-2h-4V4h10v10z"/>
              </svg>
            </button>
          </div>
          <div class="px-6 py-4">
            <TimelydoTextArea :value="embed_profile_code"
                              rows="4"
                              :placeholder="I18N.website_content.account_settings.embed.title"
                              name="user[embed code]"
                              :opts="{id: 'embed_code_text', disabled: true}" />
          </div>
        </div>

      </div>
    </template>
  </div>
</template>

<script>
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import RequestMixin from "controllers/vue_components/mixins/request"
import Constants from "controllers/vue_components/mixins/constants"
import FlashNotifications from "controllers/vue_components/flash_toast"
import Formatting from "controllers/vue_components/mixins/formatting";
import TimelydoDropdown from "controllers/vue_components/fields/timelydo_dropdown"
import TimelydoTextArea from "controllers/vue_components/fields/timelydo_textarea"

export default {
    name: 'DevelopersEmbedCode',
    props: [],
    mixins: [RequestMixin, Constants, Formatting],
    components: {
        FlashNotifications, TimelydoDropdown, TimelydoTextArea
    },
    data() {
        return {
            embed_profile_code: "",
            selectedLanguage: `${CURRENT_LOCALE}`,
            languageDropdownOptions: {
                placeholder: '',
                classes: 'w-full',
                search_able: true,
                maximum_height: '250px',
            },
        };
    },
    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },
        updateIframeCode(src) {
            this.embed_profile_code = `<iframe src="${src}" style="height:750px;width:100%;border:none"></iframe>`;
        },
        copyText() {
            this.copyTextToClipboard(this.embed_profile_code);
            this.$refs.flash.show(this.I18N.website_content.account_settings.code_copied, "notice");
        },
        selectLanguage(e) {
            this.selectedLanguage = e.language;
        },
    },

    computed: {
        embedProfileSrcCode() {
            return `${this.useSettingsStoreObj().current_user.profile_embed_url}?locale=${this.selectedLanguage}`;
        },
    },

    watch: {
        selectedLanguage() {
            this.updateIframeCode(this.embedProfileSrcCode);
        },
    },

    created() {
        this.$watch(
            () => this.useSettingsStoreObj().init_data_loaded,
            (loaded) => {
                if (loaded) this.updateIframeCode(this.embedProfileSrcCode)
            },
            { immediate: true }
        )
    },
};
</script>
